// Pixel cursor trail: small squares snapped to a grid, fading out behind the
// pointer. Fixed canvas above the whole site; `difference` blending keeps it
// visible on both light and dark sections. Bursts given an exact `color` (a
// button's own colour, utils/celebrate.js) can't go through `difference`: they
// are drawn on a second canvas with normal blending, which only exists while
// such a burst runs (display: none otherwise) and only covers the burst's area,
// so it costs no extra full-screen layer.

const CELL = 8; // px, size of one "pixel"
const LIFE = 450; // ms before a square is fully faded
const MAX = 40;
const MAX_DOTS = 400; // hard cap on live squares, bursts included
const BURST = 14; // squares thrown out on a click
const BURST_RADIUS = 5; // in cells
// Sizes (px) the burst squares pick from; each snaps to a grid of its own size.
const BURST_SIZES = [4, 8, 8, 12, 16, 24];

// Buttons whose click is celebrated by utils/celebrate.js: the plain click burst is skipped on them.
export const CELEBRATE_TARGETS = ".ajax_add_to_cart, .js-add-grouped-to-cart, .js-subscribe-lifetime";

// Squares thrown out around a point by burst(): the defaults are the click burst.
const BURST_DEFAULTS = { count: BURST, radius: BURST_RADIUS * CELL, sizes: BURST_SIZES, life: LIFE, spread: 0, drift: 0, hold: 0 };

// Squares thrown out of a shape by burstRect(): 14 per 100px of outline, within ~17deg of their direction.
const RECT_DEFAULTS = { density: 14, cone: 0.3, radius: 110, sizes: BURST_SIZES, life: LIFE, spread: 0, drift: 0, hold: 0 };

export default class CursorTrail {
  static instance = null;

  static init() {
    // Touch / no-hover devices have no cursor to follow.
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    return (CursorTrail.instance = new CursorTrail());
  }

  constructor() {
    this.dots = [];
    this.last = null;
    this.raf = null;

    this.canvas = document.createElement("canvas");
    this.canvas.className = "cursor-trail";
    this.canvas.setAttribute("aria-hidden", "true");
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d");

    this.solidCanvas = document.createElement("canvas");
    this.solidCanvas.className = "cursor-trail cursor-trail--solid";
    this.solidCanvas.setAttribute("aria-hidden", "true");
    document.body.appendChild(this.solidCanvas);
    this.solidCtx = this.solidCanvas.getContext("2d");
    this.solidArea = null; // viewport rect the solid canvas covers while shown

    this.resize = this.resize.bind(this);
    this.onMove = this.onMove.bind(this);
    this.tick = this.tick.bind(this);

    this.resize();
    window.addEventListener("resize", this.resize);
    window.addEventListener("pointermove", this.onMove, { passive: true });
    window.addEventListener("pointerdown", (e) => this.onDown(e), { passive: true });
  }

  // A small burst of squares thrown out around the pointer on click.
  onDown(e) {
    if (e.button !== 0 || (e.pointerType && e.pointerType !== "mouse")) return;
    // Those buttons get their own burst out of their shape (utils/celebrate.js).
    if (e.target.closest?.(CELEBRATE_TARGETS)) return;
    this.burst(e.clientX, e.clientY);
  }

  // Squares thrown out around (x, y), each on its own grid. `count`, `radius` (px), `sizes`
  // (px) and `life` (ms) turn it up or down: the click is the mild default.
  // `spread` (ms) makes it a wave: a square shows up after a delay proportional to its
  // distance from the centre, so the burst propagates outwards. `drift` (px) keeps every
  // square moving outwards, along its own direction, over its life.
  burst(x, y, opts = {}) {
    const { count, radius, sizes, life, spread, drift, hold } = { ...BURST_DEFAULTS, ...opts };
    for (let i = 0; i < count; i++) {
      this.addBurstDot(x, y, Math.random() * Math.PI * 2, 0.1 + Math.random() * 0.9, radius, { sizes, life, spread, drift, hold });
    }
    this.wake();
  }

  // The same, but from the outline of a rect (a button's shape) instead of a point: squares
  // leave the edge going OUTWARDS only, so the wave propagates away from the shape.
  // `density` is squares per 100px of perimeter; `cone` the half-angle (rad) of the random
  // spread around each square's direction. Near a corner that direction leans towards the
  // diagonal (up to 45deg), so the corners fan out; it never points back across the shape.
  // `color` (optional) draws its squares in that exact colour, without blending.
  burstRect(rect, opts = {}) {
    const { density, cone, radius, sizes, life, spread, drift, hold, color } = { ...RECT_DEFAULTS, ...opts };
    const { left, top, width: w, height: h } = rect;
    const perimeter = 2 * (w + h);
    const count = Math.round((perimeter / 100) * density);
    const CORNER = 0.2; // share of a side, from each end, over which the direction leans

    for (let i = 0; i < count; i++) {
      const k = 0.1 + Math.random() * 0.9;
      // A point on the outline (clockwise from the top-left corner), its outward normal,
      // and how far along its side it is (0..1).
      const t = Math.random() * perimeter;
      let ox, oy, normal, u;
      if (t < w) [ox, oy, normal, u] = [left + t, top, -Math.PI / 2, t / w];
      else if (t < w + h) [ox, oy, normal, u] = [left + w, top + (t - w), 0, (t - w) / h];
      else if (t < 2 * w + h) [ox, oy, normal, u] = [left + w - (t - w - h), top + h, Math.PI / 2, (t - w - h) / w];
      else [ox, oy, normal, u] = [left, top + h - (t - 2 * w - h), Math.PI, (t - 2 * w - h) / h];

      // Lean towards the corner it is closest to: back end -> previous side's normal.
      const lean = Math.max(0, 1 - (1 - u) / CORNER) - Math.max(0, 1 - u / CORNER);
      const a = normal + lean * (Math.PI / 4) + (Math.random() - 0.5) * 2 * cone;
      this.addBurstDot(ox, oy, a, k, radius, { sizes, life, spread, drift, hold, color });
    }
    // Squares land at most radius + drift + a square away from the outline
    if (color) this.growSolidArea(rect, radius + drift + Math.max(...sizes));
    this.wake();
  }

  // Shows the solid canvas over `rect` expanded by `reach` (merged with the
  // area of a burst still running). Resizing clears it: fine, the whole canvas
  // is redrawn every frame.
  growSolidArea(rect, reach) {
    let l = Math.floor(rect.left - reach);
    let t = Math.floor(rect.top - reach);
    let r = Math.ceil(rect.left + rect.width + reach);
    let b = Math.ceil(rect.top + rect.height + reach);
    const cur = this.solidArea;
    if (cur) {
      l = Math.min(l, cur.l);
      t = Math.min(t, cur.t);
      r = Math.max(r, cur.r);
      b = Math.max(b, cur.b);
      if (l === cur.l && t === cur.t && r === cur.r && b === cur.b) return;
    }
    this.solidArea = { l, t, r, b };
    const c = this.solidCanvas;
    c.width = (r - l) * this.dpr;
    c.height = (b - t) * this.dpr;
    Object.assign(c.style, { display: "block", left: `${l}px`, top: `${t}px`, width: `${r - l}px`, height: `${b - t}px` });
  }

  hideSolidArea() {
    this.solidArea = null;
    this.solidCanvas.style.display = "none";
  }

  // One square of a burst: an origin, a direction `a`, and how far out it goes (k of `radius`).
  addBurstDot(ox, oy, a, k, radius, { sizes, life, spread, drift, hold, color }) {
    const cell = sizes[Math.floor(Math.random() * sizes.length)];
    this.dots.push({
      // Kept as an origin + polar coordinates: the square is placed on its own grid
      // when drawn, so it can drift.
      ox,
      oy,
      a,
      r: k * radius,
      drift,
      hold, // share of its life a square stays fully opaque (0: it fades from the start)
      // A wave starts each square when the front reaches it; without one they are born
      // "already old" by a random amount and vanish at different times.
      t: performance.now() + k * spread - (spread ? 0 : Math.random() * life * 0.6),
      cell,
      life,
      color, // set: drawn as-is on the solid canvas
      burst: true,
    });
  }

  wake() {
    if (this.raf === null) this.raf = requestAnimationFrame(this.tick);
  }

  resize() {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = window.innerWidth * this.dpr;
    this.canvas.height = window.innerHeight * this.dpr;
  }

  onMove(e) {
    if (e.pointerType && e.pointerType !== "mouse") return;
    const cell = CELL;
    const cx = Math.floor(e.clientX / cell);
    const cy = Math.floor(e.clientY / cell);
    const now = performance.now();

    // Fill the cells crossed since the last event so fast moves stay continuous.
    const from = this.last || { cx, cy };
    const steps = Math.max(Math.abs(cx - from.cx), Math.abs(cy - from.cy), 1);
    for (let i = 1; i <= steps; i++) {
      const x = Math.round(from.cx + ((cx - from.cx) * i) / steps);
      const y = Math.round(from.cy + ((cy - from.cy) * i) / steps);
      const prev = this.dots[this.dots.length - 1];
      if (prev && prev.cell === cell && prev.x === x && prev.y === y) {
        prev.t = now;
      } else {
        this.dots.push({ x, y, t: now, cell });
      }
    }
    // The trail itself is capped at MAX squares (bursts don't count towards that).
    const trail = this.dots.filter((d) => !d.burst);
    if (trail.length > MAX) {
      const drop = new Set(trail.slice(0, trail.length - MAX));
      this.dots = this.dots.filter((d) => !drop.has(d));
    }
    if (this.dots.length > MAX_DOTS) this.dots.splice(0, this.dots.length - MAX_DOTS);
    this.last = { cx, cy };

    // Loop only runs while there is something to draw; wake it up here.
    this.wake();
  }

  tick(now) {
    const { ctx, solidCtx, dpr, solidArea } = this;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    if (solidArea) {
      // Viewport coordinates, shifted to the canvas' own area
      solidCtx.setTransform(dpr, 0, 0, dpr, -solidArea.l * dpr, -solidArea.t * dpr);
      solidCtx.clearRect(solidArea.l, solidArea.t, solidArea.r - solidArea.l, solidArea.b - solidArea.t);
    }
    // With `difference`, result = |background - fill|. This is grey_800 (#dad1c3)
    // minus black_900 (#222423), so squares render as black_900 on beige.
    ctx.fillStyle = "#b8ada0";

    this.dots = this.dots.filter((d) => now - d.t < (d.life || LIFE));
    if (solidArea && !this.dots.some((d) => d.color)) this.hideSolidArea();
    for (const d of this.dots) {
      const age = now - d.t;
      if (age < 0) continue; // a wave burst: the front hasn't reached this square yet
      const life = d.life || LIFE;
      const k = 1 - age / life; // 1 -> 0
      const size = Math.max(2, Math.round(d.cell * k));
      const off = Math.floor((d.cell - size) / 2);
      let gx = d.x;
      let gy = d.y;
      if (d.burst) {
        // Polar -> grid of the square's own size, so it can drift outwards.
        const r = d.r + d.drift * (age / life);
        gx = Math.floor((d.ox + Math.cos(d.a) * r) / d.cell);
        gy = Math.floor((d.oy + Math.sin(d.a) * r) / d.cell);
      }
      const target = d.color && this.solidArea ? solidCtx : ctx;
      if (d.color) target.fillStyle = d.color;
      target.globalAlpha = Math.min(1, d.hold ? k / (1 - d.hold) : k);
      target.fillRect(gx * d.cell + off, gy * d.cell + off, size, size);
    }

    if (this.dots.length) {
      this.raf = requestAnimationFrame(this.tick);
    } else {
      this.raf = null;
      this.last = null; // next move starts a fresh trail instead of bridging from a stale cell
    }
  }
}
